import React from 'react';
import { Button } from 'components/Button';
import { useUser } from 'context/user-context';
import { canManageRequests } from 'types/resolution';
import { isShipaidAdmin } from 'utils';
import { useOrderEditingWorkspace } from './useOrderEditingWorkspace';
import { actionLabel } from './expandedSettings';
import {
  Address,
  cardClass,
  displayDate,
  money,
  orderEditingFee,
  Status,
} from './WorkspaceSections';
import { ChangeSummary, OperationalAnalytics } from './ExpandedWorkspace';
import type { OrderEditingWorkspace } from 'types/order-editing';

const reportDate = (value: unknown, end = false) => {
  if (typeof value !== 'string' || !value.trim()) return 'Not available';
  const date = new Date(new Date(value).getTime() - (end ? 1 : 0));
  return Number.isFinite(date.getTime())
    ? date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
      })
    : 'Not available';
};

const operationOrderNumber = (value: unknown) =>
  (typeof value === 'string' &&
    value.match(/^gid:\/\/shopify\/Order\/([0-9]+)$/)?.[1]) ||
  'Not available';

type ActivityProps = {
  storeId: string;
  periodStart: string;
  periodEnd: string;
};
export function OrderEditingActivity({
  storeId,
  periodStart,
  periodEnd,
}: ActivityProps) {
  const { role, user } = useUser();
  const allowed = canManageRequests(role, isShipaidAdmin(user));
  const days = (Date.parse(periodEnd) - Date.parse(periodStart)) / 86400000;
  const validPeriod = Number.isFinite(days) && days >= 1 && days <= 93;
  const state = useOrderEditingWorkspace(storeId, {
    period: { periodStart, periodEnd },
    enabled: Boolean(storeId && allowed && validPeriod),
  });
  const busy =
    state.loading ||
    state.saving ||
    state.configuring ||
    Boolean(state.checking || state.reviewing || state.acknowledging);
  if (!storeId) return <p>Select a store to view Order Editing activity.</p>;
  if (!allowed)
    return <p>An Admin or Manager role is required to view this workspace.</p>;
  if (!validPeriod)
    return (
      <p
        role="alert"
        className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
      >
        Choose a date range of 1 to 93 days for Order Editing activity. Update
        the report dates above.
      </p>
    );
  const workspace =
    state.workspace?.storeId === storeId &&
    Date.parse(state.workspace.usage.periodStart) === Date.parse(periodStart) &&
    Date.parse(state.workspace.usage.periodEnd) === Date.parse(periodEnd)
      ? state.workspace
      : undefined;
  return (
    <div className="space-y-4">
      {state.error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
          <p>{state.error}</p>
          <Button
            className="mt-3"
            variant="outlined"
            disabled={busy}
            onClick={() => {
              void state.load(false);
            }}
          >
            Retry Order Editing
          </Button>
        </div>
      )}
      {state.notice && (
        <p
          role="status"
          className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800"
        >
          {state.notice}
        </p>
      )}
      {state.loading || (!workspace && !state.error) ? (
        <p role="status" className={cardClass}>
          Loading Order Editing…
        </p>
      ) : (
        workspace && (
          <OrderEditingActivityView
            workspace={workspace}
            busy={busy}
            checking={state.checking}
            acknowledging={state.acknowledging}
            onReconcile={state.reconcile}
            onAcknowledgeRouting={state.acknowledgeRouting}
          />
        )
      )}
    </div>
  );
}

type ActivityViewProps = {
  workspace: OrderEditingWorkspace;
  busy: boolean;
  checking?: string;
  acknowledging?: string;
  onReconcile: (operationId: string) => void | Promise<unknown>;
  onAcknowledgeRouting: (operationId: string) => void | Promise<unknown>;
};
export function OrderEditingActivityView({
  workspace,
  busy,
  checking,
  acknowledging,
  onReconcile,
  onAcknowledgeRouting,
}: ActivityViewProps) {
  return (
    <section
      id="order-edit-usage"
      className={cardClass}
      aria-labelledby="usage-heading"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="usage-heading" className="text-base font-semibold">
          Usage & activity
        </h2>
        <Status value={workspace.setup.billing.status} />
      </div>
      <p className="mt-2 text-sm text-gray-600">
        {orderEditingFee(workspace.setup.billing.priceCents)} USD per
        successfully edited order through your existing usage-based billing.
        Repeat edits and cancellations are free.
      </p>
      {workspace.setup.billing.status !== 'ready' && (
        <p className="mt-1 text-xs text-gray-500">
          {workspace.setup.billing.detail}
        </p>
      )}
      <p className="mt-4 text-xs text-gray-600">
        Showing {reportDate(workspace.usage.periodStart)} –{' '}
        {reportDate(workspace.usage.periodEnd, true)} (UTC)
      </p>
      <dl className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-5">
        {(
          [
            ['Edited orders', workspace.usage.editedOrders],
            ['Charged orders', workspace.usage.chargedOrders],
            ['Usage charges', money(workspace.usage.amountCents)],
            ['Pending charges', workspace.usage.pendingCharges],
            ['Charges needing review', workspace.usage.needsReviewCharges],
          ] as const
        ).map(([label, value]) => (
          <div key={label} className="rounded-md bg-gray-50 p-3">
            <dt className="text-xs text-gray-600">{label}</dt>
            <dd className="mt-1 text-xl font-semibold">{value}</dd>
          </div>
        ))}
      </dl>
      <h3 className="mt-6 text-sm font-semibold">
        Recent operations in this period
      </h3>
      <p className="mt-1 text-xs text-gray-500">
        Checking a status reconciles an existing request. It does not submit
        another edit, cancellation or uncertain charge.
      </p>
      {!Array.isArray(workspace.operations) ? (
        <p className="mt-4 rounded-md bg-amber-50 p-4 text-sm text-amber-800">
          Order activity unavailable.
        </p>
      ) : !workspace.operations.length ? (
        <p className="mt-4 rounded-md bg-gray-50 p-4 text-sm text-gray-600">
          No order edits or cancellations in this period.
        </p>
      ) : (
        <div className="mt-4 space-y-3">
          {workspace.operations.map((operation) => (
            <article
              key={operation.id}
              className="rounded-md border border-gray-200 p-4"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h4 className="text-sm font-medium">
                    {actionLabel(operation.action)} · Order{' '}
                    {operationOrderNumber(operation.orderId)}
                  </h4>
                  <p className="mt-1 text-xs text-gray-500">
                    {displayDate(operation.createdAt)} · Policy{' '}
                    {operation.policyVersion || 'not recorded'}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    {operation.actor?.type === 'guest'
                      ? 'Guest verified by email'
                      : operation.actor?.type === 'customer'
                      ? 'Signed-in customer'
                      : 'Legacy customer record'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Status value={operation.status} />
                  {operation.needsReview && <Status value="needs_review" />}
                  {(operation.status === 'pending' ||
                    operation.needsReview ||
                    operation.billingStatus === 'retryable' ||
                    operation.billingStatus === 'pending') && (
                    <Button
                      variant="outlined"
                      disabled={busy}
                      isLoading={checking === operation.id}
                      onClick={() => {
                        void onReconcile(operation.id);
                      }}
                    >
                      Check operation status
                    </Button>
                  )}
                </div>
              </div>
              {operation.action === 'address' && (
                <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                  <div>
                    <p className="mb-1 text-xs font-medium text-gray-500">
                      Before
                    </p>
                    <Address value={operation.beforeAddress} />
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium text-gray-500">
                      After
                    </p>
                    <Address value={operation.afterAddress} />
                  </div>
                </div>
              )}
              {operation.action === 'cancel' &&
                operation.cancellationReason && (
                  <p className="mt-3 text-sm">
                    Reason: {operation.cancellationReason}
                  </p>
                )}
              {operation.action !== 'address' &&
                operation.action !== 'cancel' && (
                  <div className="mt-4 space-y-3 text-sm">
                    {operation.summary && <p>{operation.summary}</p>}
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <p className="mb-1 text-xs font-medium text-gray-500">
                          Before
                        </p>
                        <ChangeSummary value={operation.before} />
                      </div>
                      <div>
                        <p className="mb-1 text-xs font-medium text-gray-500">
                          After
                        </p>
                        <ChangeSummary value={operation.after} />
                      </div>
                    </div>
                  </div>
                )}
              {operation.settlement &&
                operation.settlement.status !== 'none' && (
                  <div className="mt-4 rounded-md bg-gray-50 p-3 text-sm">
                    <div className="flex flex-wrap items-center gap-3">
                      <Status value={operation.settlement.status} />
                      <span>
                        {operation.settlement.currency}{' '}
                        {operation.settlement.amount}
                      </span>
                    </div>
                    {operation.settlement.status === 'refund_required' && (
                      <p className="mt-2 text-xs text-gray-600">
                        A refund is due. This status does not mean that a refund
                        has been issued.
                      </p>
                    )}
                    {operation.settlement.status === 'payment_required' && (
                      <p className="mt-2 text-xs text-gray-600">
                        The customer must complete the additional payment
                        through Shopify.
                      </p>
                    )}
                  </div>
                )}
              {operation.action === 'shipping' && operation.routing && (
                <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
                  <p>
                    {operation.routing.carrier} ·{' '}
                    {operation.routing.serviceCode}
                  </p>
                  {operation.routing.confirmed ? (
                    <p className="mt-2 text-xs">
                      Fulfillment service confirmed by your team.
                    </p>
                  ) : (
                    operation.status === 'succeeded' && (
                      <>
                        <p className="mt-2 text-xs">
                          Confirm your fulfillment team has applied this
                          service. Fulfillment remains on hold until payment and
                          this confirmation are complete.
                        </p>
                        <Button
                          className="mt-3"
                          variant="outlined"
                          disabled={busy}
                          isLoading={acknowledging === operation.id}
                          onClick={() => {
                            void onAcknowledgeRouting(operation.id);
                          }}
                        >
                          Confirm service applied
                        </Button>
                      </>
                    )
                  )}
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                <span>Order usage charge:</span>
                {operation.action === 'cancel' ? (
                  <span>Free cancellation</span>
                ) : operation.billingStatus ? (
                  <Status value={operation.billingStatus} />
                ) : (
                  <span>
                    {operation.status === 'succeeded' && !operation.changed
                      ? operation.action === 'address'
                        ? 'No charge — address unchanged'
                        : 'No charge — unchanged'
                      : 'No charge recorded'}
                  </span>
                )}
              </div>
              {!Array.isArray(operation.warnings) ? (
                <div className="mt-3 text-xs text-amber-800">
                  <p>Warnings unavailable.</p>
                  <p className="mt-2">Support reference: {operation.id}</p>
                </div>
              ) : (
                !!operation.warnings.length && (
                  <div className="mt-3 text-xs text-amber-800">
                    <ul className="list-disc space-y-1 pl-4">
                      {operation.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                    <p className="mt-2">Support reference: {operation.id}</p>
                  </div>
                )
              )}
            </article>
          ))}
        </div>
      )}
      <OperationalAnalytics analytics={workspace.analytics} />
      <details className="mt-5 border-t border-gray-200 pt-4">
        <summary className="cursor-pointer text-sm font-medium">
          Policy version history
        </summary>
        {!Array.isArray(workspace.policyHistory) ? (
          <p className="mt-3 text-sm text-amber-800">
            Policy history unavailable.
          </p>
        ) : workspace.policyHistory.length ? (
          <ul className="mt-3 space-y-2 text-sm text-gray-600">
            {workspace.policyHistory.map((entry) => (
              <li key={entry.revision}>
                Version {entry.revision} · {displayDate(entry.changedAt)}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-gray-500">
            No saved policy changes yet.
          </p>
        )}
      </details>
    </section>
  );
}
